import { useEffect, useMemo, useState } from 'react';
import { Card, Skeleton } from '../../components/UI';
import { MiniChart } from '../../components/MiniChart';
import { fetchReportData, listenAccounts, listenBudgets, listenMonthTransactions, listenUpcomingPlans } from '../../firebase/repo';
import { addMonths, displayDate, formatMoney, monthKey, todayISO } from '../../utils/date';
import { buildDashboardModel, type DashboardAttentionItem } from '../../utils/dashboard';
import { buildFinancialHealthModel, type FinancialHealthModel } from '../../utils/financialHealth';
import type { Account, AppNotification, Budget, Plan, Transaction } from '../../types';
import { AccountLedgerModal, AccountTools } from '../accounts/AccountTools';
import type { PageKey } from '../../app/AppShell';

type PeriodMode = 'thisMonth' | 'lastMonth' | 'fy' | 'custom';

const periodKey = (mode: PeriodMode, customMonth: string) => {
  const current = monthKey(todayISO());
  if (mode === 'lastMonth') return addMonths(`${current}-01`, -1).slice(0, 7);
  if (mode === 'custom') return customMonth || current;
  return current;
};

const periodLabel = (mode: PeriodMode, key: string) => {
  if (mode === 'lastMonth') return 'Last Month';
  if (mode === 'fy') return 'This Financial Year';
  if (mode === 'custom') return key;
  return 'This Month';
};

function MoneyStat({ label, value, tone }: { label: string; value: number; tone?: 'green' | 'red' | 'blue' | 'orange' }) {
  return <div className={`money-stat ${tone || ''}`}><span>{label}</span><b>{formatMoney(value)}</b></div>;
}

function AttentionItem({ item, onNavigate }: { item: DashboardAttentionItem; onNavigate: (page: PageKey) => void }) {
  const target: PageKey = item.action.includes('plan') || item.action.includes('plans') ? 'upcoming' : item.action.includes('budget') ? 'setup' : item.action.includes('transaction') ? 'records' : 'home';
  return <button className={`attention-item ${item.tone}`} onClick={() => onNavigate(target)}>
    <span><b>{item.label}</b><small>{item.reason}</small></span>
    <strong>{item.value}</strong>
  </button>;
}

export function DashboardPage({ uid, onNavigate, notifications = [], unreadCount = 0, onOpenNotifications }: { uid: string; onNavigate: (page: PageKey) => void; notifications?: AppNotification[]; unreadCount?: number; onOpenNotifications?: () => void }) {
  const [periodMode, setPeriodMode] = useState<PeriodMode>('thisMonth');
  const [customMonth, setCustomMonth] = useState(monthKey(todayISO()));
  const key = periodKey(periodMode, customMonth);
  const [tx, setTx] = useState<Transaction[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [ledgerAccount, setLedgerAccount] = useState<Account | null>(null);
  const [loadingTx, setLoadingTx] = useState(true);
  const [health, setHealth] = useState<FinancialHealthModel | null>(null);

  useEffect(() => {
    setLoadingTx(true);
    return listenMonthTransactions(uid, key, (rows) => { setTx(rows); setLoadingTx(false); });
  }, [uid, key]);
  useEffect(() => listenUpcomingPlans(uid, setPlans), [uid]);
  useEffect(() => listenBudgets(uid, key, setBudgets), [uid, key]);
  useEffect(() => listenAccounts(uid, setAccounts), [uid]);
  useEffect(() => {
    let active = true;
    fetchReportData(uid)
      .then((reportData) => { if (active) setHealth(buildFinancialHealthModel(reportData, todayISO())); })
      .catch(() => { if (active) setHealth(null); });
    return () => { active = false; };
  }, [uid]);

  const data = useMemo(() => buildDashboardModel({ transactions: tx, plans, budgets, accounts, periodLabel: periodLabel(periodMode, key), today: todayISO() }), [accounts, budgets, key, periodMode, plans, tx]);
  const primaryInsight = data.insights[0] || 'No urgent trend found yet.';
  const topAccount = data.liquidAccounts[0];
  const criticalAlerts = notifications.filter((item) => item.priority === 'critical').length;

  return <div className="page dashboard-page dashboard-command">
    <Card className="dashboard-hero">
      <div className="dashboard-hero-head">
        <div>
          <p className="eyebrow">Financial command center</p>
          <h1>Dashboard</h1>
          <p className="muted">Your current financial position and upcoming commitments.</p>
        </div>
        <button className="btn primary" onClick={() => onNavigate('add')}>Add Transaction</button>
      </div>
      <div className="period-row" aria-label="Reporting period">
        {([
          ['thisMonth', 'This Month'],
          ['lastMonth', 'Last Month'],
          ['fy', 'This Financial Year'],
          ['custom', 'Custom']
        ] as Array<[PeriodMode, string]>).map(([mode, label]) => <button key={mode} className={periodMode === mode ? 'active' : ''} disabled={mode === 'fy'} title={mode === 'fy' ? 'Use Reports for full financial-year analysis. Dashboard stays month-scoped for realtime performance.' : ''} onClick={() => setPeriodMode(mode)}>{label}</button>)}
        {periodMode === 'custom' && <input type="month" value={customMonth} onChange={(event) => setCustomMonth(event.target.value)} />}
        <span className="pill">{key}</span>
      </div>
      {loadingTx ? <Skeleton /> : <div className="financial-hero-grid">
        <div className="balance-panel">
          <span>Total available balance</span>
          <strong>{formatMoney(data.totalAvailableBalance)}</strong>
          <small>Bank, cash and wallet balances only. Credit-card limits are separate.</small>
          {data.liquidAccounts.some((account) => account.isNegative) && <em>Negative account balance needs attention.</em>}
        </div>
        <div className="money-grid">
          <MoneyStat label="Income" value={data.income} tone="green" />
          <MoneyStat label="Expense" value={data.expense} tone="red" />
          <MoneyStat label="Investment" value={data.investment} tone="blue" />
          <MoneyStat label="Net cash flow" value={data.netCashFlow} tone={data.netCashFlow < 0 ? 'orange' : 'green'} />
        </div>
      </div>}
      <p className="insight-text">{primaryInsight}</p>
    </Card>

    <Card className="attention-card">
      <div className="section-title"><div><h3>Needs Attention</h3><p className="muted">Only active issues from current Dashboard data.</p></div><div className="inline-actions"><button className="btn small" onClick={() => onNavigate('health')}>Review Health</button><button className="btn small" onClick={() => onNavigate('monthlyClose')}>Monthly Close</button></div></div>
      <div className={`health-summary-strip ${health?.status === 'Critical' ? 'critical' : health?.status === 'Needs Attention' ? 'warning' : health?.status === 'Review Recommended' ? 'info' : 'healthy'}`}>
        <div><b>Financial Health: {health?.status || 'Checking'}</b><span>{health ? `${health.issueCounts.critical} critical / ${health.issueCounts.warning} warning / ${health.issueCounts.info} info` : 'Loading diagnostic summary...'}</span></div>
        <button className="btn small" onClick={() => onNavigate('health')}>Review Health</button>
      </div>
      {data.attention.length ? <div className="attention-list">{data.attention.map((item) => <AttentionItem key={item.id} item={item} onNavigate={onNavigate} />)}</div> : <p className="compact-empty">No urgent financial issues found.</p>}
    </Card>

    {(unreadCount > 0 || criticalAlerts > 0) && <Card className="notification-summary-card">
      <div>
        <p className="eyebrow">Smart alerts</p>
        <h3>{criticalAlerts ? `${criticalAlerts} critical alert${criticalAlerts === 1 ? '' : 's'}` : `${unreadCount} unread alert${unreadCount === 1 ? '' : 's'}`}</h3>
        <p className="muted">Payment reminders, budget thresholds, card/account alerts, and reconciliation signals.</p>
      </div>
      <button className="btn primary" onClick={onOpenNotifications}>View Notifications</button>
    </Card>}

    <Card className="commitment-card">
      <div className="section-title"><div><h3>Upcoming commitments</h3><p className="muted">Next 15 days and overdue plan pressure.</p></div><button className="btn small" onClick={() => onNavigate('upcoming')}>View all plans</button></div>
      <div className="metric-strip">
        <div className="metric orange"><span>Next 15 days</span><b>{formatMoney(data.upcomingTotal)}</b><small>{data.upcoming.length} payment(s)</small></div>
        <div className="metric debit"><span>Overdue</span><b>{formatMoney(data.overdueTotal)}</b><small>{data.overdue.length} item(s)</small></div>
        <div className="metric"><span>Nearest due</span><b>{data.nearestDueDate ? displayDate(data.nearestDueDate) : '-'}</b><small>{data.upcoming[0]?.title || 'No due item'}</small></div>
        <div className="metric credit"><span>Card outstanding</span><b>{formatMoney(data.creditOutstanding)}</b><small>Available {formatMoney(data.availableCredit)}</small></div>
      </div>
      <div className="compact-list">{data.upcoming.slice(0, 4).map((plan) => <button key={plan.id} onClick={() => onNavigate('upcoming')}><span><b>{plan.title}</b><small>{displayDate(plan.nextDueDate || plan.dueDate)} / {plan.category}</small></span><strong>{formatMoney(plan.amount)}</strong></button>)}</div>
      {!data.upcoming.length && !data.overdue.length && <p className="compact-empty">No upcoming or overdue payments in this window.</p>}
    </Card>

    <Card className="accounts-overview">
      <div className="section-title"><div><h3>Accounts overview</h3><p className="muted">Balances and current-period linked movement.</p></div><button className="btn small" onClick={() => onNavigate('setup')}>View all accounts</button></div>
      <div className="account-card-grid">
        {data.liquidAccounts.slice(0, 6).map((account) => <div className={`dashboard-account-card ${account.isNegative ? 'negative' : ''}`} key={account.id}>
          <div><b>{account.name}</b><span>{account.institutionName || 'Institution not set'}{account.last4 ? ` / ****${account.last4}` : ''}</span></div>
          <strong>{formatMoney(account.currentBalance)}</strong>
          <div className="account-movement"><span>Credit {formatMoney(account.periodCredit)}</span><span>Debit {formatMoney(account.periodDebit)}</span><span>Net {formatMoney(account.periodNet)}</span></div>
          <p>{account.isNegative ? 'Negative balance' : 'Audit: available in account tools'}</p>
          <button className="btn small" onClick={() => setLedgerAccount(account)}>Open Statement</button>
        </div>)}
      </div>
      {!data.liquidAccounts.length && <p className="compact-empty">No bank, cash or wallet accounts yet.</p>}
      {topAccount && <p className="insight-text">Top liquid account: {topAccount.name} with {formatMoney(topAccount.currentBalance)}.</p>}
    </Card>

    <Card className="cards-overview">
      <div className="section-title"><div><h3>Credit cards</h3><p className="muted">Outstanding and utilization are separate from cash balance.</p></div></div>
      <div className="account-card-grid">
        {data.creditCards.map((card) => <div className={`dashboard-card-card ${card.utilizationState}`} key={card.id}>
          <div><b>{card.name}</b><span>{card.institutionName || 'Institution not set'}{card.last4 ? ` / ****${card.last4}` : ''}</span></div>
          <strong>{formatMoney(card.outstanding)}</strong>
          <small>Outstanding</small>
          <div className="budget-meter"><i style={{ width: `${Math.min(card.utilizationPct, 140)}%` }} /></div>
          <div className="account-movement"><span>{card.utilizationPct}% used</span><span>Available {formatMoney(card.availableCredit)}</span><span>Limit {formatMoney(card.creditLimit || 0)}</span></div>
          <button className="btn small" onClick={() => setLedgerAccount(card)}>View Statement</button>
        </div>)}
      </div>
      {!data.creditCards.length && <p className="compact-empty">No credit cards saved. Add one in Settings when needed.</p>}
      <AccountTools uid={uid} accounts={accounts} compact />
      <AccountLedgerModal uid={uid} account={ledgerAccount} accounts={accounts} open={!!ledgerAccount} onClose={() => setLedgerAccount(null)} />
    </Card>

    <Card className="recent-activity">
      <div className="section-title"><div><h3>Recent activity</h3><p className="muted">Latest current-period transactions.</p></div><button className="btn small" onClick={() => onNavigate('records')}>View all transactions</button></div>
      <div className="activity-list">
        {data.recentTransactions.slice(0, 7).map((row) => <button key={row.id} onClick={() => onNavigate('records')}>
          <span className={`type-dot ${row.type.toLowerCase()}`} />
          <span><b>{row.category}</b><small>{displayDate(row.dateISO || row.date)} / {row.accountName || 'Unassigned'} / {row.type}</small>{row.note && <em>{row.note}</em>}</span>
          <strong className={row.type === 'Income' ? 'green' : 'red'}>{row.type === 'Income' ? '+' : '-'}{formatMoney(row.amount)}</strong>
        </button>)}
      </div>
      {!data.recentTransactions.length && <p className="compact-empty">No transactions in this period yet.</p>}
    </Card>

    <Card className="insights-card">
      <div className="section-title"><div><h3>Spending and budget insights</h3><p className="muted">Top categories and budget health for {key}.</p></div><button className="btn small" onClick={() => onNavigate('reports')}>View report</button></div>
      <div className="insight-layout">
        <div>
          {data.topCategories.map((item) => <div className="category-insight" key={item.category}><div><b>{item.category}</b><span>{item.pct}% of spending</span></div><strong>{formatMoney(item.amount)}</strong></div>)}
          {!data.topCategories.length && <p className="compact-empty">No spending categories yet.</p>}
        </div>
        <div>
          {data.budgetUsage.length ? <div className={`budget-row ${data.budgetUsedPct >= 100 ? 'crossed' : data.budgetUsedPct >= 80 ? 'warning' : 'safe'}`}><div className="budget-row-head"><b>Monthly budget used</b><span>{data.budgetUsedPct}%</span></div><div className="budget-meter"><i style={{ width: `${Math.min(data.budgetUsedPct, 140)}%` }} /></div><p><span>Limit {formatMoney(data.budgetTotalLimit)}</span><span>Spent {formatMoney(data.budgetTotalSpent)}</span></p></div> : <p className="compact-empty">No budgets created for this month.</p>}
          <button className="btn small" onClick={() => onNavigate('setup')}>Set Monthly Budget</button>
        </div>
      </div>
    </Card>

    <Card className="dashboard-charts">
      <div className="section-title"><div><h3>Trend snapshot</h3><p className="muted">Compact movement chart with rule-based interpretation.</p></div></div>
      {data.trend.length ? <MiniChart rows={data.trend} /> : <p className="compact-empty">No chart data yet.</p>}
      <div className="insight-stack">{data.insights.slice(0, 3).map((text) => <p className="insight-text" key={text}>{text}</p>)}</div>
    </Card>
  </div>;
}
